import Swiper from "swiper";
import { publicCity } from "./public-city.js";
import { A11y, FreeMode, Keyboard, Mousewheel } from "swiper/modules";
import "swiper/css";
import "swiper/css/free-mode";

const chrispAvatarUrl = new URL("../assets/where/chrisp-avatar.jpg", import.meta.url).href;
const EMPTY_LOCATION = {
  status: "pending",
  city: "Location pending",
  region: "",
  country: "",
  countryCode: "",
  latitude: null,
  longitude: null,
  updatedAt: null,
};

const countryAliases = {
  BO: "Bolivia",
  CD: "Dem. Rep. Congo",
  CG: "Congo",
  CI: "Côte d'Ivoire",
  CZ: "Czechia",
  DO: "Dominican Rep.",
  FK: "Falkland Is.",
  GB: "United Kingdom",
  IR: "Iran",
  KP: "North Korea",
  KR: "South Korea",
  LA: "Laos",
  MD: "Moldova",
  MK: "North Macedonia",
  PS: "Palestine",
  RU: "Russia",
  SY: "Syria",
  TZ: "Tanzania",
  US: "United States of America",
  VE: "Venezuela",
  VN: "Vietnam",
};

const elements = {
  freshness: document.querySelector("[data-freshness]"),
  items: document.querySelector("[data-timeline-items]"),
  map: document.querySelector("[data-globe]"),
  timeline: document.querySelector("[data-timeline]"),
  title: document.querySelector("[data-title]"),
  titleContext: document.querySelector("[data-title-context]"),
  titleSubject: document.querySelector("[data-title-subject]"),
};
let globe = null;
let queuedMapUpdate = null;

async function initializeGlobe() {
  await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

  try {
    const { createTravelGlobe } = await import("./globe.js");
    globe = await createTravelGlobe(elements.map, { avatarUrl: chrispAvatarUrl });
    if (queuedMapUpdate) globe?.update(queuedMapUpdate);
  } catch {
    // The timeline remains usable when WebGL or the globe bundle is unavailable.
  }
}

function localDateKey(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function safeNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function countryFlag(countryCode) {
  const code = String(countryCode || "").trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return "";

  return [...code]
    .map((letter) => String.fromCodePoint(127397 + letter.charCodeAt(0)))
    .join("");
}

function normalizeLocation(payload) {
  return publicCity({
    ...EMPTY_LOCATION,
    ...payload,
    latitude: safeNumber(payload?.latitude),
    longitude: safeNumber(payload?.longitude),
  });
}

async function fetchJson(pathname, fallback) {
  try {
    const response = await fetch(`${pathname}?t=${Date.now()}`, { cache: "no-store" });
    if (!response.ok) throw new Error(`Request failed with ${response.status}`);
    return await response.json();
  } catch {
    return fallback;
  }
}

function monthAndYear(dateString) {
  if (!dateString) return { month: "Now", year: "" };
  const [year, month] = dateString.split("-");
  const date = new Date(Number(year), Math.max(0, Number(month) - 1), 1);
  return {
    month: new Intl.DateTimeFormat("en-US", { month: "short" }).format(date),
    year,
  };
}

function publicRegion(location) {
  return [location.region, location.country]
    .filter(Boolean)
    .filter((value, index, values) => values.indexOf(value) === index)
    .join(" · ");
}

function locationMetaMarkup(node, flag) {
  const country = String(node.country || "").trim();
  const regionParts = String(node.region || "")
    .split("·")
    .map((part) => part.trim())
    .filter(Boolean)
    .filter((part) => part !== country);
  const region = regionParts.join(" · ");
  const countryMarkup = country
    ? `<span class="where-card-country">
        ${flag ? `<span class="where-card-flag" aria-hidden="true">${flag}</span>` : ""}
        <span>${escapeHtml(country)}</span>
      </span>`
    : flag
      ? `<span class="where-card-country"><span class="where-card-flag" aria-hidden="true">${flag}</span></span>`
      : "";
  const separator = region && countryMarkup
    ? '<span class="where-card-region-separator" aria-hidden="true">·</span>'
    : "";

  return `
    ${region ? `<span>${escapeHtml(region)}</span>` : ""}
    ${separator}
    ${countryMarkup}
  `;
}

function makeTimeline(location, travel) {
  const today = localDateKey();
  const trips = Array.isArray(travel?.trips) ? travel.trips : [];
  const past = trips
    .filter((trip) => trip.end && (
      trip.end < today || (trip.end === today && trip.status === "completed")
    ))
    .sort((a, b) => a.start.localeCompare(b.start))
    .map((trip) => ({ ...trip, kind: "trip", when: "past", date: trip.start }));
  const future = trips
    .filter((trip) => trip.start && trip.start > today)
    .sort((a, b) => a.start.localeCompare(b.start))
    .map((trip) => ({ ...trip, kind: "trip", when: "future", date: trip.start }));
  const currentTrip = trips.find(
    (trip) => trip.start && trip.end && trip.start <= today && today <= trip.end,
  );
  const hasPublishedLocation = location.status === "published" && location.city;
  const current = {
    kind: "now",
    when: "now",
    date: today,
    place: hasPublishedLocation
      ? location.city
      : currentTrip?.place || EMPTY_LOCATION.city,
    region: hasPublishedLocation
      ? publicRegion(location)
      : currentTrip?.region || "Waiting for the first MacBook update",
    latitude: hasPublishedLocation ? location.latitude : safeNumber(currentTrip?.latitude),
    longitude: hasPublishedLocation ? location.longitude : safeNumber(currentTrip?.longitude),
    country: hasPublishedLocation ? location.country : currentTrip?.country,
    countryCode: hasPublishedLocation ? location.countryCode : currentTrip?.countryCode,
    updatedAt: location.updatedAt,
  };

  return [...past, current, ...future];
}

function nodeMarkup(node) {
  const { month, year } = monthAndYear(node.date);
  const isNow = node.kind === "now";
  const status = node.when === "past"
    ? "Completed"
    : node.status === "tentative"
      ? "Tentative"
      : "Confirmed";
  const label = isNow
    ? `<div class="where-card-label-row">
        <p class="where-card-label">Right now</p>
        <p class="where-card-freshness" data-freshness aria-live="polite">Checking in</p>
      </div>`
    : "";
  const flag = countryFlag(node.countryCode);
  const locationMeta = locationMetaMarkup(node, flag);
  const place = `<h2 class="where-card-place">${escapeHtml(node.place)}</h2>`;
  const meta = isNow
    ? ""
    : `<div class="where-card-meta">
        <span class="where-card-status${node.status === "tentative" ? " where-card-status--tentative" : ""}">${status}</span>
      </div>`;

  return `
    <div class="swiper-slide">
      <article class="where-node${isNow ? " where-node--now" : ""}">
        <time class="where-node-date" datetime="${escapeHtml(node.date)}">
          <span class="where-node-month">${escapeHtml(month)}</span>
          <span class="where-node-year">${escapeHtml(year)}</span>
        </time>
        <div class="where-node-body">
          <div class="where-card">
            <div>
              ${label}
              ${place}
              <p class="where-card-region">${locationMeta}</p>
            </div>
            ${meta}
          </div>
        </div>
      </article>
    </div>
  `;
}

function titleForNode(node) {
  if (node.when === "past") return { context: "where was", subject: "chrisp?" };
  if (node.when === "future") return { context: "where is", subject: "chrisp going?" };
  return { context: "where is", subject: "chrisp?" };
}

function countryNameForNode(node) {
  return countryAliases[node.countryCode] || node.country || "";
}

function updateMap(node, previousNode = null, shouldAnimateRoute = true) {
  queuedMapUpdate = {
    node,
    previousNode,
    activeCountryName: countryNameForNode(node),
    animateRoute: shouldAnimateRoute,
  };
  globe?.update(queuedMapUpdate);
}

function updateFreshness(node) {
  if (!elements.freshness) return;
  if (node.when !== "now") return;
  if (!node.updatedAt) {
    elements.freshness.textContent = "Waiting for the first check-in";
    return;
  }

  const updated = new Date(node.updatedAt);
  const elapsedMinutes = Math.max(0, Math.round((Date.now() - updated.getTime()) / 60000));
  if (elapsedMinutes < 2) {
    elements.freshness.textContent = "Updated just now";
  } else if (elapsedMinutes < 60) {
    elements.freshness.textContent = `Updated ${elapsedMinutes} minutes ago`;
  } else {
    const hours = Math.round(elapsedMinutes / 60);
    elements.freshness.textContent = `Updated ${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  }
}

function setActiveNode(nodes, index, previousIndex = null) {
  const activeNode = nodes[index] || nodes[0];
  const previousNode = previousIndex === null ? null : nodes[previousIndex];
  document.querySelectorAll(".where-node").forEach((node, nodeIndex) => {
    node.classList.toggle("is-active", nodeIndex === index);
    node.classList.toggle("is-before", nodeIndex < index);
    node.classList.toggle("is-after", nodeIndex > index);
  });

  if (elements.title && elements.titleContext && elements.titleSubject) {
    const title = titleForNode(activeNode);
    elements.titleContext.textContent = title.context;
    elements.titleSubject.textContent = title.subject;
    elements.title.style.animation = "none";
    requestAnimationFrame(() => elements.title.style.removeProperty("animation"));
  }
  updateMap(activeNode, previousNode);
  updateFreshness(activeNode);
}

async function init() {
  const [locationPayload, travel] = await Promise.all([
    fetchJson("./location.json", EMPTY_LOCATION),
    fetchJson("./travel.json", { trips: [] }),
  ]);
  const location = normalizeLocation(locationPayload);
  const nodes = makeTimeline(location, travel);
  const currentIndex = nodes.findIndex((node) => node.kind === "now");

  if (!elements.items || !elements.timeline) return null;
  elements.items.innerHTML = nodes.map(nodeMarkup).join("");
  elements.freshness = document.querySelector("[data-freshness]");

  let activeIndex = Math.max(0, currentIndex);
  const swiper = new Swiper(elements.timeline, {
    modules: [A11y, FreeMode, Keyboard, Mousewheel],
    direction: "vertical",
    centeredSlides: true,
    slidesPerView: "auto",
    initialSlide: activeIndex,
    slideToClickedSlide: true,
    speed: 480,
    keyboard: { enabled: true, onlyInViewport: false },
    mousewheel: {
      forceToAxis: true,
      releaseOnEdges: true,
      sensitivity: 1.1,
    },
    freeMode: {
      enabled: true,
      momentum: true,
      momentumBounce: false,
      momentumRatio: 0.72,
      momentumVelocityRatio: 0.86,
      sticky: true,
    },
    a11y: {
      enabled: true,
      containerMessage: "chrisp travel timeline",
    },
    on: {
      afterInit(instance) {
        instance.slideTo(activeIndex, 0, false);
        const previousIndex = activeIndex > 0 ? activeIndex - 1 : null;
        setActiveNode(nodes, activeIndex, previousIndex);
      },
      slideChange(instance) {
        const previousIndex = instance.previousIndex;
        activeIndex = instance.activeIndex;
        setActiveNode(nodes, activeIndex, previousIndex);
      },
    },
  });

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") updateFreshness(nodes[activeIndex]);
  });
  window.setInterval(() => updateFreshness(nodes[activeIndex]), 60000);
  initializeGlobe();

  return swiper;
}

init().catch(() => {
  if (elements.freshness) elements.freshness.textContent = "Location temporarily unavailable";
});
